import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, ArrowLeft, ExternalLink, Share2, Mail, Copy, Check, X } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '@/i18n/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const XTwitterIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ShareMenu = ({ article, t }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);
  const url = window.location.href;
  const title = article.title;
  const excerpt = article.excerpt || '';

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const shareOptions = [
    {
      label: 'LinkedIn',
      icon: <LinkedInIcon />,
      color: '#0A66C2',
      onClick: () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=500'),
    },
    {
      label: 'WhatsApp',
      icon: <WhatsAppIcon />,
      color: '#25D366',
      onClick: () => window.open(`https://wa.me/?text=${encodeURIComponent(title + ' — ' + url)}`, '_blank'),
    },
    {
      label: 'X / Twitter',
      icon: <XTwitterIcon />,
      color: '#000',
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400'),
    },
    {
      label: 'Email',
      icon: <Mail size={18} />,
      color: '#EA4335',
      onClick: () => window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(excerpt + '\n\n' + url)}`,
    },
    {
      label: t.articleDetail.copyLink || 'Copier le lien',
      icon: copied ? <Check size={18} /> : <Copy size={18} />,
      color: 'var(--accent-color)',
      onClick: () => {
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
    },
  ];

  return (
    <div className="share-menu-wrapper" ref={menuRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(!open)}
        className="share-trigger-btn"
        data-testid="article-share-btn"
      >
        <Share2 size={16} />
        {t.articleDetail.share}
      </Button>
      {open && (
        <div className="share-dropdown" data-testid="share-dropdown">
          <div className="share-dropdown-header">
            <span>{t.articleDetail.share}</span>
            <button className="share-close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
          </div>
          {shareOptions.map((opt) => (
            <button
              key={opt.label}
              className="share-option"
              onClick={() => { opt.onClick(); if (opt.label !== (t.articleDetail.copyLink || 'Copier le lien')) setOpen(false); }}
              data-testid={`share-${opt.label.toLowerCase().replace(/[^a-z]/g, '')}`}
            >
              <span className="share-option-icon" style={{ color: opt.color }}>{opt.icon}</span>
              <span className="share-option-label">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t, lang, switchLanguage } = useLanguage();

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/articles/${slug}/`);
      setArticle(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching article:', err);
      setError(t.articleDetail.notFound);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticle();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="article-detail-loading">
        <p>{t.articleDetail.loading}</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article-detail-error">
        <h2>{t.articleDetail.notFound}</h2>
        <p>{error}</p>
        <Button onClick={() => navigate('/articles')}>
          <ArrowLeft size={18} className="mr-2" />
          {t.articleDetail.backToArticles}
        </Button>
      </div>
    );
  }

  return (
    <div className="article-detail-page">
      {/* Back button */}
      <div className="article-detail-nav">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/articles')}
          className="back-button"
          data-testid="article-detail-back-btn"
        >
          <ArrowLeft size={18} />
          {t.articleDetail.backToArticles}
        </Button>
        <div className="language-switcher" data-testid="detail-lang-switcher">
          <button onClick={() => switchLanguage('fr')} className={`lang-flag ${lang === 'fr' ? 'active' : ''}`} title="Français" aria-label="Français">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" width="28" height="19"><rect width="900" height="600" fill="#ED2939"/><rect width="600" height="600" fill="#fff"/><rect width="300" height="600" fill="#002395"/></svg>
          </button>
          <button onClick={() => switchLanguage('en')} className={`lang-flag ${lang === 'en' ? 'active' : ''}`} title="English" aria-label="English">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="28" height="19"><clipPath id="s3"><path d="M0,0 v30 h60 v-30 z"/></clipPath><clipPath id="t3"><path d="M30,15 h30 v15 z v15 h-30 z h-30 v-15 z v-15 h30 z"/></clipPath><g clipPath="url(#s3)"><path d="M0,0 v30 h60 v-30 z" fill="#012169"/><path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6"/><path d="M0,0 L60,30 M60,0 L0,30" clipPath="url(#t3)" stroke="#C8102E" strokeWidth="4"/><path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10"/><path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6"/></g></svg>
          </button>
        </div>
      </div>

      {/* Article Header */}
      <header className="article-detail-header">
        <div className="article-detail-meta">
          <span className="article-category-badge">{article.category}</span>
          {article.article_type === 'news' && (
            <span className="article-type-badge news-badge">
              <ExternalLink size={14} />
              {t.articles.news}
            </span>
          )}
        </div>
        
        <h1 className="article-detail-title">{article.title}</h1>
        
        <div className="article-detail-info">
          <span className="info-item">
            <Calendar size={16} />
            {formatDate(article.published_at)}
          </span>
          {article.article_type === 'article' && (
            <span className="info-item">
              <Clock size={16} />
              {article.reading_time} {t.articleDetail.readingTime}
            </span>
          )}
          <span className="info-item author">
            {t.articleDetail.by} {article.author}
          </span>
        </div>

        <ShareMenu article={article} t={t} />
      </header>

      {/* Article Image */}
      {article.image_url && (
        <div className="article-detail-image-container">
          <img 
            src={article.image_url} 
            alt={article.title} 
            className="article-detail-image"
          />
        </div>
      )}

      {/* Article Content */}
      <article className="article-detail-content">
        {article.article_type === 'news' && article.external_url && (
          <div className="article-external-notice">
            <ExternalLink size={18} />
            <p>
              {t.articleDetail.externalNotice}{' '}
              <a href={article.external_url} target="_blank" rel="noopener noreferrer">
                {t.articleDetail.readOriginal}
              </a>
            </p>
          </div>
        )}

        <div className="markdown-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {article.content}
          </ReactMarkdown>
        </div>
      </article>

      {/* Article Footer */}
      <footer className="article-detail-footer">
        <Button onClick={() => navigate('/articles')} className="back-button">
          <ArrowLeft size={18} className="mr-2" />
          {t.articleDetail.backToArticles}
        </Button>
        <ShareMenu article={article} t={t} />
      </footer>
    </div>
  );
};

export default ArticleDetail;
